import { useState } from "react";
import { ScoreBadge, VerdictPill } from "./VerdictBadge";
import { cn } from "../lib/utils";
import { toast } from "sonner";
import { api } from "../lib/api";
import { ChevronDown, DollarSign, Lock, AlertTriangle, X } from "lucide-react";

export function BuyCard({ vehicle, onAction }) {
  const [expanded, setExpanded] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const act = async (action, override_justification = null) => {
    setSubmitting(true);
    try {
      await api.post("/decisions", {
        vin: vehicle.vin, action, override_justification,
        max_bid: vehicle.target_price,
      });
      toast.success(
        action === "alert_ucm"
          ? `UCM alerted · max bid locked at $${vehicle.target_price.toLocaleString()}`
          : `Decision logged · ${action}`,
      );
      if (onAction) onAction();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to log decision");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid={`buy-card-${vehicle.vin}`}
      className={cn(
        "rounded-xl p-4 md:p-5 transition-all relative",
        `verdict-${vehicle.verdict}`,
      )}
    >
      <div className="flex items-start gap-4">
        <ScoreBadge score={vehicle.total_score} verdict={vehicle.verdict} size="lg" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <VerdictPill verdict={vehicle.verdict} />
            <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
              {vehicle.source.replace("_", " ")}
            </span>
          </div>
          <div className="text-lg font-semibold text-navy leading-tight truncate">
            {vehicle.year} {vehicle.make} {vehicle.model} <span className="text-slate-600 font-medium">{vehicle.trim}</span>
          </div>
          <div className="text-xs text-slate-500 font-medium mt-0.5">
            {vehicle.mileage.toLocaleString()} mi · {vehicle.exterior_color} · {vehicle.drivetrain} · {vehicle.source_detail}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Target</div>
          <div className="text-xl font-bold tabular text-navy leading-tight">${vehicle.target_price.toLocaleString()}</div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mt-1">Proj gross</div>
          <div className="text-lg font-bold tabular text-verdict-strongText leading-tight">
            +${vehicle.projected_gross.toLocaleString()}
          </div>
        </div>
      </div>

      <button
        data-testid={`expand-${vehicle.vin}`}
        onClick={() => setExpanded((e) => !e)}
        className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-navy transition-colors"
      >
        <ChevronDown size={14} className={cn("transition-transform", expanded && "rotate-180")} />
        {expanded ? "Hide" : "Why"}
      </button>

      {expanded && (
        <div className="mt-3 pt-3 border-t border-slate-200/60">
          <ul className="space-y-1.5 mb-4">
            {vehicle.justification.map((j, i) => (
              <li key={i} className="text-sm text-slate-700 flex gap-2">
                <span className="text-freddy-red font-bold">·</span>
                <span>{j}</span>
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap gap-2">
            <button
              data-testid={`alert-ucm-${vehicle.vin}`}
              disabled={submitting}
              onClick={() => act("alert_ucm")}
              className="inline-flex items-center gap-1.5 bg-freddy-red hover:bg-freddy-redDark text-white text-xs font-bold px-3 py-2 rounded-md transition-colors disabled:opacity-50"
            >
              <AlertTriangle size={12} />
              Alert UCM · lock max ${vehicle.target_price.toLocaleString()}
            </button>
            <button
              data-testid={`bid-${vehicle.vin}`}
              disabled={submitting}
              onClick={() => act("bid")}
              className="inline-flex items-center gap-1.5 bg-navy hover:bg-navy-800 text-white text-xs font-bold px-3 py-2 rounded-md transition-colors disabled:opacity-50"
            >
              <Lock size={12} /> Log bid
            </button>
            <button
              data-testid={`pass-${vehicle.vin}`}
              disabled={submitting}
              onClick={() => act("passed")}
              className="inline-flex items-center gap-1.5 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs font-bold px-3 py-2 rounded-md transition-colors disabled:opacity-50"
            >
              <X size={12} /> Pass
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
